import React, { useState, useRef, useEffect } from "react";
import { Cancel } from "@mui/icons-material";

/**
 * Filter bar above the /all-resources grid.
 *
 * How it works: hosts four filter controls and emits the consolidated
 * `filters` shape up to the parent via `onChange` whenever the admin
 * tweaks anything. The parent (`AllResources`) is responsible for
 * translating that shape into Firestore `.where()` clauses on
 * `initialQuery` (server-side) plus client-side post-filters on the
 * fetched page (for the cases Firestore can't represent — see notes).
 *
 * Filter shape:
 *   {
 *     status: "all" | "active" | "inactive",        // server-side
 *     talent: "all" | "talents" | "non-talents",    // talents → server; non-talents → client
 *     hasRates: "all" | "yes" | "no",               // client-side only (hourlyRate is computed at fetch)
 *     vendor: null | { vendorID, companyName, companyLogo },  // server-side
 *   }
 *
 * @param {object} props
 * @param {object} props.filters - Current filter state owned by the parent.
 * @param {(next: object) => void} props.onChange - Called whenever any
 *   control changes. Parent updates its state which feeds back into
 *   `props.filters` to keep the UI controlled.
 * @param {object} props.firebase - Firebase wrapper used for the vendor
 *   typeahead (same `getDocumentsByArrayMembershipInCollection` call
 *   that `MigrateDevs.js` uses).
 * @returns {JSX.Element}
 *
 * How to test: render with empty filters; type a vendor name and assert
 *   the dropdown populates via the firebase mock; click a vendor and
 *   assert onChange fires with `vendor: { vendorID, ... }`; click a
 *   status pill and assert onChange fires with the new status.
 */
const AllResourcesFilterBar = ({ filters, onChange, firebase }) => {
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorMatches, setVendorMatches] = useState([]);
  const searchDebounceRef = useRef(null);

  const set = (patch) => onChange({ ...filters, ...patch });

  /**
   * Resets every filter back to its "all"/null default.
   * @returns {void}
   */
  const clearAll = () => {
    onChange({
      status: "all",
      talent: "all",
      hasRates: "all",
      vendor: null,
    });
    setVendorSearch("");
    setVendorMatches([]);
  };

  /**
   * Debounced vendor-name typeahead. Fires after 250ms of no typing so
   * we don't hammer Firestore on every keystroke.
   *
   * @param {string} text - Raw input value.
   * @returns {void}
   */
  const onVendorSearchChange = (text) => {
    setVendorSearch(text);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!text.trim()) {
      setVendorMatches([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      const matches =
        await firebase.getDocumentsByArrayMembershipInCollection(
          "vendors",
          text.toLowerCase(),
          ["nameIndex"],
          "companyName",
          false,
          true,
          false,
          5
        );
      setVendorMatches(matches || []);
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const hasAnyFilterActive =
    filters.status !== "all" ||
    filters.talent !== "all" ||
    filters.hasRates !== "all" ||
    filters.vendor != null;

  const showClientOnlyHint =
    filters.hasRates !== "all" || filters.talent === "non-talents";

  return (
    <div className="all-resources-filter-bar">
      <div className="all-resources-filter-bar__group">
        <span className="all-resources-filter-bar__label">Status</span>
        <div className="all-resources-filter-bar__segmented">
          {[
            { v: "all", label: "All" },
            { v: "active", label: "Active" },
            { v: "inactive", label: "Inactive" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              className={`all-resources-filter-bar__pill ${
                filters.status === opt.v ? "is-active" : ""
              }`}
              onClick={() => set({ status: opt.v })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="all-resources-filter-bar__group">
        <span className="all-resources-filter-bar__label">Talent</span>
        <div className="all-resources-filter-bar__segmented">
          {[
            { v: "all", label: "All" },
            { v: "talents", label: "Talents" },
            { v: "non-talents", label: "Non-talents" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              className={`all-resources-filter-bar__pill ${
                filters.talent === opt.v ? "is-active" : ""
              }`}
              onClick={() => set({ talent: opt.v })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="all-resources-filter-bar__group">
        <span className="all-resources-filter-bar__label">Rates</span>
        <div className="all-resources-filter-bar__segmented">
          {[
            { v: "all", label: "All" },
            { v: "yes", label: "With rates" },
            { v: "no", label: "No rates" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              className={`all-resources-filter-bar__pill ${
                filters.hasRates === opt.v ? "is-active" : ""
              }`}
              onClick={() => set({ hasRates: opt.v })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="all-resources-filter-bar__group all-resources-filter-bar__group--vendor">
        <span className="all-resources-filter-bar__label">Vendor</span>
        {filters.vendor ? (
          <div className="all-resources-filter-bar__vendor-chip">
            {filters.vendor.companyLogo && (
              <img
                src={filters.vendor.companyLogo}
                alt={filters.vendor.companyName}
              />
            )}
            <span>{filters.vendor.companyName}</span>
            <Cancel
              className="all-resources-filter-bar__vendor-chip-clear"
              onClick={() => {
                set({ vendor: null });
                setVendorSearch("");
                setVendorMatches([]);
              }}
            />
          </div>
        ) : (
          <div
            className="all-resources-filter-bar__vendor-input-wrapper"
            style={{ position: "relative", flex: "1 1 auto", minWidth: 220 }}
          >
            <input
              key="vendor-search-input"
              type="text"
              name="vendorSearch"
              autoComplete="off"
              placeholder="Type a vendor name..."
              value={vendorSearch}
              onChange={(e) => onVendorSearchChange(e.target.value)}
              className="all-resources-filter-bar__vendor-input"
              style={{ width: "100%", padding: "7px 10px" }}
            />
            {vendorMatches.length > 0 && (
              <div className="all-resources-filter-bar__vendor-dropdown">
                {vendorMatches.map((vendor) => (
                  <div
                    key={vendor.id}
                    className="all-resources-filter-bar__vendor-option"
                    onClick={() => {
                      set({
                        vendor: {
                          vendorID: vendor.id,
                          companyName: vendor.data()?.companyName,
                          companyLogo: vendor.data()?.logo?.url,
                        },
                      });
                      setVendorSearch("");
                      setVendorMatches([]);
                    }}
                  >
                    {vendor.data()?.logo?.url && (
                      <img
                        src={vendor.data()?.logo?.url}
                        alt={vendor.data()?.companyName}
                      />
                    )}
                    <span>{vendor.data()?.companyName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {hasAnyFilterActive && (
        <button
          type="button"
          className="all-resources-filter-bar__clear-all"
          onClick={clearAll}
        >
          Clear all filters
        </button>
      )}

      {showClientOnlyHint && (
        <div className="all-resources-filter-bar__hint">
          ⓘ Rates and Non-talents filter the current page only.
        </div>
      )}
    </div>
  );
};

export default AllResourcesFilterBar;
